import React, { useEffect, useState } from 'react';
import { Loader, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createAiAssistedControl,
  recommendControlsForRisk,
  type RecommendedControlSuggestion,
} from '../../../request/controls/recommendForRisk.ts';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import axiosInstance from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';

export type AiRecommendRiskContext = {
  riskName: string;
  riskDescription: string;
  riskCategory: string;
  riskLevel: string;
};

export type AiRecommendProcessTarget = {
  processUuid: string;
  /** Business process_id for GET /api/processes/{process_id} (not the UUID). */
  routeProcessId?: string;
  processLabel: string;
};

export type AcceptedAiControl = {
  id: string;
  label: string;
};

type AiRecommendControlsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  riskContext: AiRecommendRiskContext;
  processTarget: AiRecommendProcessTarget | null;
  onAccepted: (processUuid: string, control: AcceptedAiControl) => void;
};

type SuggestionRow = RecommendedControlSuggestion & {
  key: string;
  status: 'pending' | 'accepted' | 'rejected' | 'accepting';
  acceptedControlId?: string;
};

function truncateLabel(title: string, controlId: string): string {
  const prefix = controlId || 'CTL';
  const combined = `${prefix} - ${title}`;
  return combined.length > 48 ? `${combined.slice(0, 45)}…` : combined;
}

export const AiRecommendControlsModal: React.FC<AiRecommendControlsModalProps> = ({
  isOpen,
  onClose,
  riskContext,
  processTarget,
  onAccepted,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<SuggestionRow[]>([]);

  useEffect(() => {
    if (!isOpen || !processTarget) return undefined;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      setRows([]);
      try {
        let processName = processTarget.processLabel;
        let processDescription = '';
        // GET /api/processes/{id} resolves business process_id, not processes.id UUID.
        const lookupId = String(processTarget.routeProcessId || '').trim();
        if (lookupId) {
          try {
            const processResp = await axiosInstance.get(API_ENDPOINTS.getProcessById(lookupId));
            const processData =
              processResp?.data?.data ?? processResp?.data?.process ?? processResp?.data;
            if (processData) {
              processName =
                String(processData.name ?? processData.process_name ?? processName).trim() ||
                processName;
              processDescription = String(processData.description ?? '').trim();
            }
          } catch {
            // Fall back to label-only process context.
          }
        }

        const suggestions = await recommendControlsForRisk(
          {
            risk_name: riskContext.riskName,
            risk_description: riskContext.riskDescription,
            risk_category: riskContext.riskCategory,
            risk_level: riskContext.riskLevel,
          },
          {
            name: processName,
            description: processDescription,
          }
        );
        if (cancelled) return;
        setRows(
          suggestions.map((item, index) => ({
            ...item,
            key: `${index}-${item.title}`,
            status: 'pending',
          }))
        );
      } catch (err) {
        if (cancelled) return;
        const message = resolve403ErrorMessage(
          err,
          err instanceof Error ? err.message : 'Failed to generate control recommendations'
        );
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, processTarget, riskContext]);

  useEffect(() => {
    if (!isOpen || loading || error || rows.length === 0) return;
    const allResolved = rows.every((row) => row.status === 'accepted' || row.status === 'rejected');
    if (allResolved) {
      onClose();
    }
  }, [error, isOpen, loading, onClose, rows]);

  if (!isOpen || !processTarget) return null;

  const handleAccept = async (key: string) => {
    const row = rows.find((item) => item.key === key);
    if (!row || row.status !== 'pending') return;

    setRows((prev) =>
      prev.map((item) => (item.key === key ? { ...item, status: 'accepting' } : item))
    );
    try {
      const created = await createAiAssistedControl({
        title: row.title,
        controlWordingText: row.control_wording_text,
      });
      const label = truncateLabel(row.title, created.control_id || created.client_control_id);
      onAccepted(processTarget.processUuid, { id: created.id, label });
      setRows((prev) =>
        prev.map((item) =>
          item.key === key
            ? { ...item, status: 'accepted', acceptedControlId: created.control_id || created.id }
            : item
        )
      );
      toast.success(`Control ${created.control_id || 'created'} accepted`);
    } catch (err) {
      setRows((prev) =>
        prev.map((item) => (item.key === key ? { ...item, status: 'pending' } : item))
      );
      toast.error(
        resolve403ErrorMessage(err, err instanceof Error ? err.message : 'Failed to create control')
      );
    }
  };

  const handleReject = (key: string) => {
    setRows((prev) =>
      prev.map((item) => (item.key === key ? { ...item, status: 'rejected' } : item))
    );
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-amber-200/80 bg-white shadow-[0_24px_64px_-16px_rgba(180,83,9,0.28)]">
        <div className="relative overflow-hidden border-b border-amber-200/70 bg-gradient-to-br from-amber-50 via-yellow-50 to-white px-5 py-5">
          <div
            className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-amber-200/40 blur-2xl"
            aria-hidden
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-300/80 bg-amber-100/90 px-2.5 py-1 text-amber-900 shadow-sm">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white">
                  <Sparkles className="h-3 w-3" aria-hidden />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.08em]">AI recommend</span>
              </div>
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Recommended controls</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
                For process{' '}
                <span className="font-bold text-amber-900">{processTarget.processLabel}</span>.
                Accept to create and map, or reject to dismiss.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-amber-700/70 hover:bg-amber-100 hover:text-amber-950"
              aria-label="Close AI recommend popup"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-amber-50/40 to-white px-5 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-amber-900/70">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 ring-1 ring-amber-300/70">
                <Loader className="h-6 w-6 animate-spin text-amber-600" />
              </div>
              <p className="text-sm font-semibold text-slate-800">Generating control recommendations…</p>
              <p className="mt-1 text-xs text-slate-500">AI is drafting controls for this risk</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/50 p-6 text-center text-sm text-slate-500">
              No recommendations returned.
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, index) => (
                <div
                  key={row.key}
                  className="rounded-xl border border-amber-200/80 bg-white p-4 shadow-sm ring-1 ring-amber-100/80"
                >
                  <div className="mb-2.5 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center gap-1.5 text-amber-800">
                      <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <p className="text-[11px] font-bold uppercase tracking-[0.06em]">
                        Suggestion {index + 1}
                      </p>
                    </div>
                    {row.status === 'accepted' ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                        Accepted{row.acceptedControlId ? ` · ${row.acceptedControlId}` : ''}
                      </span>
                    ) : null}
                    {row.status === 'rejected' ? (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                        Rejected
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm font-bold leading-snug text-slate-900">{row.title}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
                    {row.control_wording_text}
                  </p>
                  {row.status === 'pending' || row.status === 'accepting' ? (
                    <div className="mt-3.5 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleReject(row.key)}
                        disabled={row.status === 'accepting'}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleAccept(row.key)}
                        disabled={row.status === 'accepting'}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3.5 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-amber-600 disabled:opacity-50"
                      >
                        {row.status === 'accepting' ? (
                          <>
                            <Loader className="h-3.5 w-3.5 animate-spin" />
                            Creating…
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3.5 w-3.5" aria-hidden />
                            Accept
                          </>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiRecommendControlsModal;
